/**
 * Hoe lang een gekozen opname duurt, zonder hem te uploaden.
 *
 * De grens in de keten is speelduur en niet bestandsgrootte: alles gaat eerst
 * door de normalisatie naar Opus (zie `MAX_OPNAME_SECONDEN` in shared). Een
 * video van een gigabyte die veertig minuten duurt gaat prima; een audiobestand
 * van 300 MB dat vier uur duurt niet. Zonder deze controle merkt de buddy dat
 * pas ná het uploaden, ná de omzetting en ná een betaalde transcriptiepoging.
 *
 * De browser leest dit uit de metadata, dus er wordt hooguit een kopstukje van
 * het bestand gelezen en niets verstuurd.
 */

/** Zonder deze grens blijft een bestand dat de browser niet kan openen voor
 *  altijd "nog aan het lezen". Tien seconden is ruim voor metadata. */
const GEDULD_MS = 10_000;

/**
 * `null` als de duur niet te bepalen is. Dat is geen fout maar een uitkomst:
 * niet elke browser kan elk formaat openen, en een opname van een MediaRecorder
 * meldt vaak `Infinity`. In beide gevallen laten we hem door — de Worker keurt
 * hem na de omzetting alsnog af als hij echt te lang is.
 */
export function leesDuur(bestand: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(bestand);
    // Eén element voor audio én video: een <video> leest allebei, en een
    // Teams-opname is een videobestand waar we alleen het geluid van willen.
    const el = document.createElement('video');
    let klaar = false;

    const af = (duur: number | null) => {
      if (klaar) return;
      klaar = true;
      clearTimeout(wekker);
      el.removeAttribute('src');
      URL.revokeObjectURL(url);
      resolve(duur);
    };

    const wekker = setTimeout(() => af(null), GEDULD_MS);
    el.preload = 'metadata';
    el.onloadedmetadata = () =>
      af(Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null);
    el.onerror = () => af(null);
    el.src = url;
  });
}
